import { z } from "zod";

// Base schema for different search methods
export const baseSearchSchema = z.object({
  searchMethod: z.enum(["udise", "govtId", "address"], {
    message: "Please select a search method",
  }),
});

// UDISE search schema
export const udiseSearchSchema = baseSearchSchema.extend({
  searchMethod: z.literal("udise"),
  udiseCode: z
    .string()
    .min(3, "Enter 3 characters to start searching")
    .regex(/^\d+$/, "UDISE code must contain only numbers"),
});

// Government School ID search schema
export const govtIdSearchSchema = baseSearchSchema.extend({
  searchMethod: z.literal("govtId"),
  govtSchoolId: z
    .string()
    .min(3, "Enter 3 characters to start searching")
    .regex(/^\d+$/, "Govt. school ID must contain only numbers"),
});

// Address search schemas
export const pincodeSearchSchema = z.object({
  searchMethod: z.literal("address"),
  addressSearchMethod: z.literal("pincode"),
  pincode: z
    .string()
    .min(6, "Pincode must be exactly 6 digits")
    .max(6, "Pincode must be exactly 6 digits")
    .regex(/^\d+$/, "Pincode must contain only numbers"),
  schoolName: z
    .string()
    .min(2, "School name must be at least 2 characters")
    .max(100, "School name is too long"),
});

export const locationSearchSchema = z.object({
  searchMethod: z.literal("address"),
  addressSearchMethod: z.literal("location"),
  state: z.string().min(1, "Please select a state"),
  district: z.string().min(1, "Please select a district"),
  city: z.string().min(1, "Please select a city"),
  pincode: z
    .string()
    .min(6, "Pincode must be exactly 6 digits")
    .max(6, "Pincode must be exactly 6 digits")
    .regex(/^\d+$/, "Pincode must contain only numbers"),
  schoolName: z
    .string()
    .min(2, "School name must be at least 2 characters")
    .max(100, "School name is too long"),
});

export const addressSearchSchema = z.discriminatedUnion("addressSearchMethod", [
  pincodeSearchSchema,
  locationSearchSchema,
]);

// Combined schema using discriminated union
export const schoolRegistrationSchema = z.discriminatedUnion("searchMethod", [
  udiseSearchSchema,
  govtIdSearchSchema,
  addressSearchSchema,
]);

// Export types
export type SchoolRegistrationFormData = z.infer<
  typeof schoolRegistrationSchema
>;
export type UdiseSearchData = z.infer<typeof udiseSearchSchema>;
export type GovtIdSearchData = z.infer<typeof govtIdSearchSchema>;
export type AddressSearchData = z.infer<typeof addressSearchSchema>;
export type PincodeSearchData = z.infer<typeof pincodeSearchSchema>;
export type LocationSearchData = z.infer<typeof locationSearchSchema>;

// Validation helper functions
export const validateFormData = (data: unknown) => {
  return schoolRegistrationSchema.safeParse(data);
};

export const getSchemaForSearchMethod = (searchMethod: string) => {
  switch (searchMethod) {
    case "udise":
      return udiseSearchSchema;
    case "govtId":
      return govtIdSearchSchema;
    case "address":
      return addressSearchSchema;
    default:
      return baseSearchSchema;
  }
};
